import VueRouter from 'vue-router';
import Vue from 'vue';
import i18n from '@/i18n';

Vue.use(VueRouter);

const routes = [
  {
    path: '/',
    component: () => import('@/components/template/Template.vue'),
    meta: { title: '' },
    children: [
      {
        path: '',
        meta: { title: 'welcome' },
        component: () => import('@/components/Welcome.vue'),
        beforeEnter: (to, from, next) => {
          if (isLoggedIn()) {
            next({ name: 'Dashboard' });
          } else {
            next();
          }
        },
      },
      {
        name: 'LogIn',
        path: 'login',
        meta: { title: 'login.title' },
        component: () => import('@/components/authentication/LogIn.vue'),
      },
      {
        name: 'ForgotPassword',
        path: 'forgot-password',
        meta: { title: 'password.forgot' },
        component: () => import('@/components/authentication/ForgotPassword.vue'),
      },
      {
        name: 'Dashboard',
        path: '/dashboard',
        meta: { title: 'dashboard', requiresAuth: true },
        component: () => import('@/components/DashboardContent.vue'),
      },
      {
        name: 'Profile',
        path: 'profile',
        meta: { title: 'profile.title', requiresAuth: true },
        component: () => import('@/components/profile/Profile.vue'),
      },
      {
        name: 'Avatar',
        path: '',
        meta: { title: 'avatar.title', requiresAuth: true },
        component: () => import('@/components/profile/Avatar.vue'),
      },
      {
        name: 'Helper',
        path: '/helper',
        component: () => import('@/components/Helper.vue'),
        meta: {
          title: 'helper.title',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/' },
            { text: 'User Guide & Tutorial', to: '/helper' },
          ],
        },
      },
      {
        name: 'Assets',
        path: '/assets',
        meta: { title: 'asset.title', requiresAuth: true },
        component: () => import('@/modules/asset/views/Dashboard.vue'),
      },
      {
        name: 'Privacy',
        path: '/privacy-page',
        meta: { title: 'Privacy Policy' },
        component: () => import('@/components/PrivacyPage.vue'),
      },
      {
        name: 'Cookies',
        path: '/cookies-page',
        meta: { title: 'Cookies Policy' },
        component: () => import('@/components/CookiesPage.vue'),
      },
      {
        name: 'SignUp',
        path: 'sign-up',
        meta: { title: 'sign-up' },
        component: () => import('@/components/authentication/SignUp.vue'),
      },
      {
        name: 'ConformityAssessment',
        path: '/conformityAssessment',
        component: () => import('@/modules/conformityAssessment/views/ConformityAssessment.vue'),
        meta: {
          title: 'conformityAssessment.title',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/' },
            { text: 'Compliance Assessment', to: '/conformityAssessment' },
          ],
        },
      },
      {
        name: 'AssessmentResults',
        path: '/assessmentResults',
        component: () => import('@/modules/conformityAssessment/views/AssessmentResults.vue'),
        meta: {
          title: 'assessmentResults.title',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/' },
            { text: 'Compliance Assessment', to: '/conformityAssessment' },
            { text: 'Assessment Results', to: '/assessmentResults' },
          ],
        },
      },
      {
        name: 'SoCList',
        path: '/soc-list',
        component: () => import('@/modules/soc/views/SoCList.vue'),
        meta: {
          title: 'soclist.title',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/' },
            { text: 'Subjects of Certification List', to: '/soc-list' },
          ],
        },
      },
      {
        name: 'SubjectOfCertification',
        path: '/subject-of-certification',
        component: () => import('@/modules/soc/views/SubjectOfCertification.vue'),
        meta: {
          title: 'Subject Of Certification',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/soc-list' },
            { text: 'Subjects of Certification', to: '/soc-list' },
            { text: 'Select Subject of Certification (SoC)', to: '/subject-of-certification' },
          ],
        },
      },
      {
        name: 'ListSpecifiedProcesses',
        path: '/listSpecifiedProcesses',
        component: () => import('@/modules/soc/views/ListSpecifiedProcesses.vue'),
        meta: {
          title: 'List Specified Processes',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/soc-list' },
            { text: 'Subjects of Certification', to: '/soc-list' },
            { text: 'Select Subject', to: '/subject-of-certification' },
            { text: 'List Specified Processes', to: '/listSpecifiedProcesses' },
          ],
        },
      },
      {
        name: 'TechnicalComponentsView',
        path: '/technicalComponentsView',
        component: () => import('@/modules/soc/views/TechnicalComponentsView.vue'),
        meta: {
          title: 'Technical Components View',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/soc-list' },
            { text: 'Subjects of Certification', to: '/soc-list' },
            { text: 'Select Subject', to: '/subject-of-certification' },
            { text: 'Technical Components', to: '/technicalComponentsView' },
          ],
        },
      },
      {
        name: 'ServiceProviderView',
        path: '/serviceProviderView',
        component: () => import('@/modules/soc/views/ServiceProviderView.vue'),
        meta: {
          title: 'ServiceProviderView',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/soc-list' },
            { text: 'Subjects of Certification', to: '/soc-list' },
            { text: 'Select Subject', to: '/subject-of-certification' },
            { text: 'IACS Service Provider Characterization', to: '/serviceProviderView' },
          ],
        },
      },
      {
        name: 'Organisation',
        path: '/organisation',
        meta: { title: 'organisation.title', requiresAuth: true },
        component: () => import('@/modules/organisation/views/Organisation.vue'),
      },

      {
        name: 'Standards',
        path: '/standards',
        meta: { title: 'standards.title', requiresAuth: true },
        component: () => import('@/modules/standards/views/Standards.vue'),
      },
      {
        name: 'AssetUploadBuilder',
        path: '/asset-upload',
        meta: { title: 'asset.upload', requiresAuth: true },
        component: () => import('@/modules/asset/views/AssetUploadBuilder.vue'),
      },

      {
        name: 'Requirements',
        path: '/requirements',
        meta: { title: 'requirements.title', requiresAuth: true },
        component: () => import('@/modules/requirements/views/Requirements.vue'),
        props: true,
      },
      {
        name: 'EvidenceList',
        path: '/evidences',
        component: () => import('@/modules/evidence/views/EvidenceList.vue'),
        meta: {
          title: 'evidence.title',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/' },
            { text: 'Evidence', to: '/evidences' },
          ],
        },
      },
      {
        name: 'EvidenceBuilder',
        path: '/evidence-create',
        meta: { title: 'evidence.create', requiresAuth: true },
        component: () => import('@/modules/evidence/views/EvidenceBuilder.vue'),
      },
      {
        name: 'Component',
        path: '/components',
        meta: { title: 'component.create', requiresAuth: true },
        component: () => import('@/modules/component/views/Component.vue'),
      },
      {
        name: 'SecurityAssuranceCase',
        path: '/security-assurance-case',
        component: () => import('@/modules/sac/views/SecurityAssuranceCase.vue'),
        meta: {
          title: 'sac.title',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/' },
            { text: 'Security Assurance Case', to: '/security-assurance-case' },
          ],
        },
      },
      {
        name: 'SacBuilder',
        path: '/security-assurance-case-builder',
        component: () => import('@/modules/sac/views/SacBuilder.vue'),
        meta: {
          title: 'sac.title',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/' },
            { text: 'Security Assurance Case', to: '/security-assurance-case' },
            { text: 'Select the Subject of Certification', to: '/security-assurance-case-builder' },
          ],
        },
      },
      {
        name: 'Claims',
        path: '/claims',
        component: () => import('@/modules/sac/views/Claims.vue'),
        meta: {
          title: 'claims.title',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/' },
            { text: 'Security Assurance Case', to: '/security-assurance-case' },
            { text: 'Select the Subject of Certification', to: '/security-assurance-case-builder' },
            { text: 'Claims', to: '/claims' },
          ],
        },
      },
      {
        name: 'SubClaims',
        path: '/sub-claims',
        component: () => import('@/modules/sac/views/SubClaims.vue'),
        meta: {
          title: 'subClaims.title',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/' },
            { text: 'Security Assurance Case', to: '/security-assurance-case' },
            { text: 'Select the Subject of Certification', to: '/security-assurance-case-builder' },
            { text: 'Claims', to: '/claims' },
            { text: 'SubClaims', to: '/sub-claims' },
          ],
        },
      },
      {
        name: 'SubClaimEvidenceMapper',
        path: '/sub-claims-evidence-mapper',
        component: () => import('@/modules/sac/views/SubClaimEvidenceMapper.vue'),
        meta: {
          title: 'Evidence Mapper',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/' },
            { text: 'Security Assurance Case', to: '/security-assurance-case' },
            { text: 'Select the Subject of Certification', to: '/security-assurance-case-builder' },
            { text: 'Claims', to: '/claims' },
            { text: 'SubClaims', to: '/sub-claims' },
            { text: 'Evidence Mapper', to: '/sub-claims-evidence-mapper' },
          ],
        },
      },
      {
        name: 'JsonView',
        path: '/json-tree',
        component: () => import('@/modules/sac/views/JsonView.vue'),
        meta: {
          title: 'sac.jsonTitle',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/' },
            { text: 'Security Assurance Case', to: '/security-assurance-case' },
            { text: 'Select the Subject of Certification', to: '/security-assurance-case-builder' },
            { text: 'JsonView', to: '/json-tree' },
          ],
        },
      },
      {
        name: 'AiAssistance',
        path: '/ai-assistance',
        component: () => import('@/modules/sac/views/AiAssistance.vue'),
        meta: {
          title: 'sac.aiAssistanceTitle',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/' },
            { text: 'Security Assurance Case', to: '/security-assurance-case' },
            { text: 'Select the Subject of Certification', to: '/security-assurance-case-builder' },
            { text: 'AiAssistance', to: '/ai-assistance' },
          ],
        },
      },
      {
        name: 'TreeView',
        path: '/tree-view',
        component: () => import('@/modules/sac/views/TreeView.vue'),
        meta: {
          title: 'sac.treeView',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/' },
            { text: 'Security Assurance Case', to: '/security-assurance-case' },
            { text: 'Select the Subject of Certification', to: '/security-assurance-case-builder' },
            { text: 'Requirements Tree', to: '/tree-view' },
          ],
        },
      },

      {
        name: 'VisualExplorer',
        path: '/visual-explorer',
        component: () => import('@/modules/sac/views/VisualExplorer.vue'),
        meta: {
          title: 'sac.visualExplorer',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/' },
            { text: 'Security Assurance Case', to: '/security-assurance-case' },
            { text: 'Visual Explorer', to: '/visual-explorer' },
          ],
        },
      },
      {
        name: 'SbomVisualizer',
        path: '/sbom-visualizer',
        component: () => import('@/modules/sbomVisualizer/SbomVisualizer.vue'),
        meta: {
          title: 'sbomVisualizer.title',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/' },
            { text: 'SBOM', to: '/sbom' },
            { text: 'SBOM Visualizer', to: '/sbom-visualizer' },
          ],
        },
      },
      {
        name: 'SacComposerChat',
        path: '/sac-composer-chat',
        component: () => import('@/modules/sac/views/SacComposerChat.vue'),
        meta: {
          title: 'sacComposer.title',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/' },
            { text: 'SAC Composer', to: '/sac-composer-chat' },
          ],
        },
      },
      {
        name: 'SacTemplateCreation',
        path: '/sac-template-editor',
        component: () => import('@/modules/sac/views/SacTemplateCreation.vue'),
        meta: {
          title: 'SAC Template Editor',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/' },
            { text: 'SAC Management', to: '/security-assurance-case' },
            { text: 'Template Editor', to: '/sac-template-editor' },
          ],
        },
      },
      {
        name: 'ThreatSpiderProjects',
        path: '/threatSpiderProjects',
        component: () => import('@/modules/threatSpider/views/ThreatSpiderProjects.vue'),
        meta: {
          title: 'Threat Spider Projects',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/' },
            { text: 'Threat Spider Projects', to: 'tThreatSpiderProjects' },
          ],
        },
      },
      {
        name: 'ThreatSpiderComponents',
        path: '/threatSpiderComponents',
        component: () => import('@/modules/threatSpider/views/ThreatSpiderComponents.vue'),
        meta: {
          title: 'Threat Spider Components',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/' },
            { text: 'Threat Spider Projects', to: '/threatSpiderProjects' },
            { text: 'Threat Spider Components', to: '/threatSpiderComponents' },
          ],
        },
      },
      {
        name: 'Sbom',
        path: '/sbom',
        component: () => import('@/modules/sbom/views/Sbom.vue'),
        meta: {
          title: 'SBOM',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/' },
            { text: 'SBOM', to: '/sbom' },
          ],
        },
      },
      {
        name: 'AdminPanel',
        path: '/admin-panel',
        component: () => import('@/modules/admin/views/AdminPanel.vue'),
        meta: {
          title: 'Admin Panel',
          requiresAuth: true,
          authorize: ['admin-manager'],
          breadcrumbs: [
            { text: 'Home', to: '/' },
            { text: 'Admin Panel', to: '/admin-panel' },
          ],
        },
      },
      {
        name: 'RagAnalyzer',
        path: '/ai-standards-assistant',
        component: () => import('@/views/RagAnalyzer.vue'),
        meta: {
          title: 'AI Standards Assistant',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/' },
            { text: 'AI Standards Assistant', to: '/ai-standards-assistant' },
          ],
        },
      },
      {
        name: 'PlantUml',
        path: '/plant-uml-editor',
        component: () => import('@/modules/plantuml/views/PlantUml.vue'),
        meta: {
          title: 'plantUml.title',
          requiresAuth: true,
          breadcrumbs: [
            { text: 'Home', to: '/' },
            { text: 'PlantUML', to: '/plant-uml-editor' },
          ],
        },
      },
      {
        path: '/page-401',
        name: 'Page401',
        component: () => import('@/views/Page401.vue'),
      },
      {
        path: '/page-403',
        name: 'Page403',
        component: () => import('@/views/Page403.vue'),
      },
      {
        path: '*',
        component: () => import('@/views/Page404.vue'),
      },
      {
        name: 'FileServer',
        path: '/fileServer',
        meta: { title: 'fileServer.title', requiresAuth: true },
        component: () => import('@/modules/asset/views/Dashboard.vue'),
      },
      {
        name: 'OnBoard',
        path: '//asset-upload',
        meta: { title: 'asset.upload', requiresAuth: true },
        component: () => import('@/modules/asset/views/AssetUploadBuilder.vue'),
      },
    ],
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
});

router.beforeEach((to, from, next) => {
  // Title
  let globalTitle = process.env.VUE_APP_TITLE;
  if (to.meta.title) {
    document.title = i18n.tc(to.meta.title, 2) + ' · ' + globalTitle;
  } else {
    document.title = globalTitle;
  }

  // Check if user is an admin
  let userRoles = [];
  try {
    // Get roles from localStorage - might be string, array, or JSON string
    const storedRoles = localStorage.roles;

    if (typeof storedRoles === 'string') {
      // Try to parse as JSON
      try {
        const parsed = JSON.parse(storedRoles);
        userRoles = Array.isArray(parsed) ? parsed : [storedRoles];
      } catch (e) {
        // Not valid JSON, treat as comma-separated string
        userRoles = storedRoles.split(',').map(role => role.trim());
      }
    } else if (Array.isArray(storedRoles)) {
      userRoles = storedRoles;
    }

    // Check if user is an admin
    const isAdmin = userRoles.some(role => role === 'admin-manager' || role === 'admin_manager');

    // If user is admin and trying to access a page other than AdminPanel, Profile, login, or root
    if (
      isAdmin &&
      to.name !== 'AdminPanel' &&
      to.name !== 'Profile' &&
      to.name !== 'Avatar' &&
      to.name !== 'LogIn' &&
      to.path !== '/'
    ) {
      console.log('Admin user redirected to AdminPanel from:', to.path);
      return next({ name: 'AdminPanel' });
    }
  } catch (error) {
    console.error('Error checking user roles:', error);
  }

  // Continue with normal authorization checks
  if (to.matched.some(record => record.meta.requiresAuth) && !loggedIn()) {
    return next({
      path: process.env.VUE_APP_LOGINPATH,
    });
  }

  // Check if route has specific role requirements
  if (to.meta.authorize) {
    console.log('Required roles for route:', to.meta.authorize);

    // Check if user has any of the required roles
    const hasAuthorizedRole = userRoles.some(role => {
      // Check against both hyphen and underscore versions of admin role
      if (
        to.meta.authorize.includes('admin-manager') &&
        (role === 'admin_manager' || role === 'admin-manager')
      ) {
        return true;
      }
      return to.meta.authorize.includes(role);
    });

    if (!hasAuthorizedRole) {
      console.log('User not authorized for this route');
      // Redirect to dashboard instead of forbidden page
      return next({ name: 'Dashboard' });
    }
  }

  // Continue with normal navigation
  let currentPathName = to.name;
  if (localStorage[currentPathName] && localStorage[currentPathName] !== to.fullPath) {
    next({
      path: localStorage[currentPathName],
    });
  } else {
    next();
  }
});
function isLoggedIn() {
  return !!localStorage.getItem('userfullname');
}
function loggedIn() {
  if (!localStorage.token || !localStorage.expires_in_timestamp) return false;

  let date_now = Date.now();
  let temp = (localStorage.expires_in_timestamp - date_now) / 1000;
  if (temp <= 0) {
    console.log('Refresh token has expired');
    return false;
  }

  return true;
}

export default router;
